import React, { useState } from 'react';
import CreateJobStep from './CreateJobStep';
import { createJob } from '../../store/actions/createJobActions';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store/actions/appDispatch';
import { CallLogData, MapCallFieldsStep } from '../../store/state/createJobState';
import CreateJobPrevStep from './CreateJobPrevStep';
import './CreateJobMapLogStep.scss';
import CreateJobLogTable from './CreateJobLogTable';
import { AppState } from '../../store/state/appState';
import {
	getAllCallFieldMappingValidationResults, 
	getErrorCallFieldValidations, 
	getWarningCallFieldValidations,
	isCurrentCreateJobStepValid
} from '../../store/selectors/createJobSelectors';
import { useHistory } from 'react-router';
import CreateJobFieldList from './CreateJobFieldList';
import classNames from 'classnames';

export default function CreateJobMapLogStep(props: Props) {
	const isStepValid = useSelector<AppState, boolean>(s => isCurrentCreateJobStepValid(s, props.engagementId));
	const dispatch = useDispatch<AppDispatch>();
	const history = useHistory();
	const [showListView, setShowListView] = useState(false);
	const [continueWasClicked, setContinueWasClicked] = useState<boolean>(false);
	const validationResults = getAllCallFieldMappingValidationResults(props.step.mappings, props.callLogData);
	const errorsToShow = validationResults.flatMap<{ field: string; message: string; }>(r => {
		return getErrorCallFieldValidations(r, continueWasClicked).map(v => ({
			field: r.mapping.displayName,
			message: v.message
		}));
	});
	const warnings = validationResults.flatMap<{ field: string; message: string; }>(r => {
		return getWarningCallFieldValidations(r).map(v => ({ 
			field: r.mapping.displayName, 
			message: v.message 
		}));
	});

	return (
		<React.Fragment>
			<CreateJobStep progressStatus="map" engagementId={props.engagementId}>
				<React.Fragment>
					<div className={classNames('create-job-map-log-step__container', { 'create-job-map-log-step__container_fixed-height-bottom-section': !showListView })}>
						<div className="create-job-map-log-step__top-section">
							<h1 className="h1-mb-1 text-center">Map your call log to Creovai</h1>

							<div className="text-center">
								Select the call log fields that match the Creovai field
							</div>

							<div className="create-job-map-log-step__buttons-section">
								<div className="flex-item-static"><button onClick={() => setShowListView(false)} className={classNames('toggle-button', 'toggle-button_left', { 'toggle-button_active': !showListView })}>Table view</button></div>
								<div className="flex-item-static"><button onClick={() => setShowListView(true)} className={classNames('toggle-button', 'toggle-button_right', { 'toggle-button_active': showListView })}>List view</button></div>
								<div className="flex-item-static create-job-map-log-step__previous-step">
									<div className="mr-4"><CreateJobPrevStep engagementId={props.engagementId} /></div>
								</div>
								<div className="flex-item-static">
									<button className="btn btn_wide-2" disabled={isContinueButtonDisabled()} onClick={handleContinue}>Continue</button>
								</div>
							</div>
							
							{(!!warnings.length || !!errorsToShow.length) &&
								<div className="create-job-map-log-step__errors-section">
									{errorsToShow.map((error, i) => (
										<div key={i} className="text-red create-job-map-log-step__error-item"><span className="text-bold">{error.field}:</span> {error.message}</div>
									))}
									{warnings.map((warning, i) => (
										<div key={i} className="create-job-map-log-step__error-item"><span className="text-bold">{warning.field}:</span> {warning.message}</div>
									))}
								</div>
							}
						</div>

						<div className="create-job-map-log-step__bottom-section">
							{!showListView && 
								<React.Fragment>
									<CreateJobLogTable callLogData={props.callLogData}
													   engagementId={props.engagementId}
													   mappings={props.step.mappings} 
													   showNonIntrusiveErrors={continueWasClicked} />
								</React.Fragment>
							}

							{showListView &&
								<React.Fragment>
									<CreateJobFieldList callLogData={props.callLogData}
														engagementId={props.engagementId}
														mappings={props.step.mappings}
														showNonIntrusiveErrors={continueWasClicked} />
								</React.Fragment>
							}
						</div>
					</div>
				</React.Fragment>
			</CreateJobStep>
		</React.Fragment>
	);
	
	function isContinueButtonDisabled(): boolean {
		if (props.isJobBeingCreated) {
			return true;
		}
		
		if (continueWasClicked) {
			return !isStepValid;
		} else {
			// if the continue button hasn't been clicked and there
			// are hidden/non-intrusive errors we need to keep it
			// enabled so the user can click it to reveal those errors.
			return !isStepValid && !validationResults.some(r => r.validations.some(v => v.type === 'error' && !!v.nonIntrusive));
		}
	}
	
	function handleContinue() {
		if (isStepValid) {
			dispatch(createJob(history, props.engagementId));
		}
		
		setContinueWasClicked(true);
	}
}

interface Props {
	callLogData: CallLogData;
    step: MapCallFieldsStep;
	engagementId: number;
	isJobBeingCreated: boolean;
}
